<template>
	<span
		class="title-poster"
		:class="{
			[`title-poster--no-radius-${noRadius}`]: noRadius,
		}"
		@click="$emit('click', $event)"
	>
		<FreeTag v-if="hasFreeOffers()" />

		<template v-if="mountImpressionPixel">
			<ImpressionPixel v-for="tag in externalImpressionTags" :key="tag" :impressionTag="tag" />
		</template>

		<TitlePosterRibbonGraphqlLite :additionalContexts="additionalContexts" v-if="showRibbon" />

		<PosterQuickActionsGraphql
			v-if="showQuickActions"
			:title="title"
			:watchNowOffer="watchNowOffer"
			:additionalContexts="additionalContexts"
			:forceRender="forceQuickActions"
			:hasMoreLikeThis="hasMoreLikeThis"
			:showWatchNowButton="showWatchNowButton"
			:isSimilarTitle="clickSource === 'similartitles'"
			:smallBubbles="smallBubbles"
			v-bind="$attrs"
		/>

		<PictureComp
			v-if="posterUrl"
			class="title-poster__image"
			v-observe-visibility="onVisibilityChanged({ title, index, onVisibilityCallback })"
			:alt="titleName"
			:imageProfile="imageProfile"
			:imageUrl="posterUrl"
			:slug="titleSlug"
			:lazy="lazy"
			:loading="loading"
			:sponsoredRecImages="sponsoredRecImages"
			@imageError="src => $emit('imageError', src)"
		/>

		<span v-else class="title-poster--no-poster">
			{{ titleName }}
		</span>

		<span v-if="titleSeasonNumber && showTvBadge" class="title-poster__badge">
			{{ $t('WEBAPP_SEASON') }} {{ titleSeasonNumber }}
		</span>

		<span v-else-if="showTVShowBadge" class="title-poster__badge">
			{{ $t('WEBAPP_SHOW_BADGE') }}
		</span>

		<span v-if="isShowAvailabilityBadge" class="title-poster__availability-badge">
			<AvailabilityBadge
				:date="availabilityBadgeDate"
				:isInWatchlist="isTitleInList(TitleListType.WatchNext, title)"
				:type="availabilityBadgeType"
				:size="availabilityBadgeSize"
				:dayCountdown="availabilityBadgeCountdown"
				:daysBeforeSoon="availabilityBadgeCountdown && 30"
			>
				<template #after-label>
					<FontAwesomeIcon :icon="faClock" />
				</template>
			</AvailabilityBadge>
		</span>

		<span
			v-if="showTVShowBadge && showNewEpisodesCount && watchNowOffer?.newElementCount"
			class="title-poster__badge__new"
		>
			<span v-if="watchNowOffer.newElementCount === 1">{{ $t('WEBAPP_NEW_EPISODE') }}</span>
			<span v-if="watchNowOffer.newElementCount > 1">
				{{ $t('WEBAPP_X_EPISODES', { episodes: watchNowOffer.newElementCount }) }}
			</span>
		</span>
	</span>
</template>

<script setup lang="ts">
import { ThumborImage } from '@/filters/thumbor';
import type { PosterSponsoredRecImages, PosterTitle } from '@/interfaces/components/poster/PosterGraphql';
import type { SnowplowContext } from '@/helpers/tracking/providers';
import type { AdditionalProperties } from '@/helpers/composables/useBatchImpressionTracking';
import type { WatchNowOfferFragment } from '@/pages/graphql/fragments/WatchNowOffer.fragment';
import type { Maybe } from '@/@types/graphql-types';

import { computed, defineAsyncComponent, ref } from 'vue';

import { MonetizationType, ObjectType, TitleListType } from '@/@types/graphql-types';

import PictureComp from '@/components/picture/PictureComp.vue';

import { diffInDays, diffInMilliseconds } from '@/helpers/date-helper';
import { usePosterImpressionTracking } from '@/helpers/composables/usePosterImpressionTracking';

import { getDisneyImpressionUrl } from '@/helpers/provider-promotion';
import { getHboImpressionUrl } from '@/helpers/impression-tracker-helper';
import { isTitleInList, isTitleSeason } from '@/helpers/title-helper';

import { faClock } from '@fortawesome/free-solid-svg-icons';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';

const FreeTag = defineAsyncComponent(() => import('./FreeTag.vue'));
const ImpressionPixel = defineAsyncComponent(
	() =>
		import(
			/* webpackChunkName: "impression-pixel" */ '@/features/sponsored-recommendations/components/ImpressionPixel.vue'
		)
);
const AvailabilityBadge = defineAsyncComponent(() => import('@/components/poster/AvailabilityBadge.vue'));
const PosterQuickActionsGraphql = defineAsyncComponent(
	() => import('@/components/poster/PosterQuickActionsGraphql.vue')
);
const TitlePosterRibbonGraphqlLite = defineAsyncComponent(
	() => import('@/components/poster/TitlePosterRibbonGraphqlLite.vue')
);

const props = withDefaults(
	defineProps<{
		additionalContexts?: SnowplowContext[];
		availabilityBadgeSize?: 'short' | 'long';
		clickSource?: string;
		forceQuickActions?: boolean;
		freeOffersCount?: number;
		hasMoreLikeThis?: boolean;
		imageProfile?: keyof ThumborImage<any>;
		index?: number;
		lazy?: boolean;
		loading?: 'lazy' | 'eager';
		moduleGroup?: string;
		noRadius?: 'top' | 'right' | 'bottom' | 'left';
		showAvailabilityBadge?: boolean;
		showFreeTag?: boolean;
		showNewEpisodesCount?: boolean;
		showQuickActions?: boolean;
		showRibbon?: boolean;
		showTvBadge?: boolean;
		showWatchNowButton?: boolean;
		smallBubbles?: boolean;
		sponsoredRecImages?: PosterSponsoredRecImages;
		title: PosterTitle;
		trackingProperties?: AdditionalProperties;
		watchNowOffer?: Maybe<WatchNowOfferFragment>;
	}>(),
	{
		freeOffersCount: 0,
		additionalContexts: () => [],
		trackingProperties: () => ({}),
		imageProfile: 'poster',
		showWatchNowButton: true,
		showTvBadge: true,
		availabilityBadgeSize: 'long',
		moduleGroup: '',
		lazy: true,
		loading: 'eager',
	}
);

defineEmits<{
	(e: 'click', event: MouseEvent): void;
	(e: 'imageError', src: string): void;
}>();

const userStore = useUserStore();
const languageStore = useLanguageStore();

function hasFreeOffers() {
	if (!props.showFreeTag) {
		return false;
	}

	const hasFreeOffersCount = props.freeOffersCount && props.freeOffersCount > 0;
	const hasFreeMonetizationType =
		props.watchNowOffer &&
		[MonetizationType.Free, MonetizationType.Ads].includes(props.watchNowOffer.monetizationType);

	return hasFreeOffersCount || hasFreeMonetizationType;
}

const posterUrl = props.title.content?.posterUrl ?? '';

const titleSlug = (() => {
	const fullPath = posterUrl?.match(/([^\/]*)$/);
	return fullPath && fullPath[0] ? fullPath[0] : 'Poster';
})();

const showTVShowBadge = props.showTvBadge && [ObjectType.Show, ObjectType.ShowSeason].includes(props.title.objectType);

const leavingOfferFromSelectedPackages = computed(() => {
	const userPackages = userStore.settings.providers;
	return (
		(props.title?.availableTo ?? [])
			?.filter(
				item =>
					item?.availableCountDown ||
					(!!item?.availableToDate && diffInDays(new Date(item.availableToDate)) < 30)
			)
			.find(item => userPackages.includes(item?.package?.shortName)) || null
	);
});

const upcomingReleaseFromSelectedPackages = computed(() => {
	const userPackages = userStore.settings.providers;
	return (
		(props.title?.content?.upcomingReleases ?? [])
			?.filter(item => !!item?.releaseDate && diffInMilliseconds(new Date(item.releaseDate)) > 0)
			.filter(item => (item?.releaseCountDown ?? diffInDays(new Date(item?.releaseDate))) < 30)
			.find(item => userPackages.includes(item?.package?.shortName!)) || null
	);
});

const availabilityBadgeDate = computed(() => {
	if (!!upcomingReleaseFromSelectedPackages.value) {
		return upcomingReleaseFromSelectedPackages.value.releaseDate;
	}
	if (!!leavingOfferFromSelectedPackages.value) {
		return leavingOfferFromSelectedPackages.value.availableToDate;
	}
	return null;
});

const isShowAvailabilityBadge = computed(
	() => props.showAvailabilityBadge && userStore.isLoggedIn && !!availabilityBadgeDate.value
);

const availabilityBadgeType = computed(() => (!!upcomingReleaseFromSelectedPackages.value ? 'upcoming' : 'leaving'));

const availabilityBadgeCountdown = computed(() => {
	if (!!upcomingReleaseFromSelectedPackages.value) {
		return upcomingReleaseFromSelectedPackages.value.releaseCountDown;
	}
	if (!!leavingOfferFromSelectedPackages.value) {
		return leavingOfferFromSelectedPackages.value.availableCountDown;
	}
	return null;
});

const titleSeasonNumber = computed(() => (isTitleSeason(props.title) ? props.title?.content?.seasonNumber : undefined));

const { t } = useI18n();
const titleName = computed(() =>
	isTitleSeason(props.title)
		? `${props.title?.show?.content?.title} - ${t('WEBAPP_SEASON')} ${titleSeasonNumber.value}`
		: props.title?.content?.title
);

// IMPRESSION PIXEL //
const mountImpressionPixel = ref(false);

const externalImpressionTags = computed(() => {
	const tags: string[] = [];
	const disneyUrl = getDisneyImpressionUrl(props.watchNowOffer?.package?.shortName, languageStore.webLocale);
	if (disneyUrl) tags.push(disneyUrl);
	const hboUrl =
		props.watchNowOffer?.package?.shortName === 'mxx' ? getHboImpressionUrl(languageStore.country) : null;
	if (hboUrl) tags.push(hboUrl);
	return tags;
});

const { onVisibilityChanged } = usePosterImpressionTracking({
	groupId: () => props.moduleGroup ?? '',
	preventDuplicateAdditionalContext: true,
	additionalContexts: () => props.additionalContexts!,
	additionalProperties: () => props.trackingProperties,
});

function onVisibilityCallback() {
	if (!mountImpressionPixel.value && externalImpressionTags.value.length > 0) {
		mountImpressionPixel.value = true;
	}
}

// IMPRESSION PIXEL END //
</script>

<style lang="scss">
@import '~@/styles/variables.scss';
@import '~@/styles/rtl-support-mixins.scss';

.title-poster {
	position: relative;
	display: flex;
	overflow: hidden;
	aspect-ratio: 166/236;
	background: $jw-color-darkblue; // displayed for titles that don’t have an image
	transition: opacity 0.1s ease;
	border-radius: $jw-title-poster-radius;

	&:active:not(&--no-click) {
		opacity: 0.5;
	}

	&__availability-badge {
		display: flex;
		position: absolute;
		top: 12px;
		@include inset-inline('end', 12px);
	}

	&--no-poster {
		width: 100%;
		height: 100%;
		position: absolute;
		top: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		text-align: center;
	}

	&__image {
		position: absolute;
		top: 0;
		height: 100%; // make it responsive to its container
		width: 100%;
		user-select: none;
		pointer-events: none;
		border-radius: $jw-title-poster-radius;
	}

	&__badge {
		color: var(--jw-color-light-shade);
		font-size: 12px;
		border-radius: $jw-border-radius-small;
		position: absolute;
		bottom: 8px;
		@include inset-inline('end', 8px);
		z-index: 0;
		text-align: end;
		background-color: $jw-color-darkblue--dark;
		padding: 1px 5px;

		&__new {
			padding: 1px 5px;
			font-size: 12px;
			color: $jw-color-white;
			background: $jw-color-red;
			position: absolute;
			@include inset-inline('end', 12px);
			bottom: 27px;
		}

		&__with-provider {
			bottom: 41px;
			@include inset-inline('end', 5px);
		}
	}

	&__button {
		transform: translateY(600px);
		width: 2500px;
	}

	&--no-radius-top {
		@include border-vertical-horizontal-radius('top', 'left', 0);
		@include border-vertical-horizontal-radius('top', 'right', 0);
	}

	&--no-radius-right {
		@include border-vertical-horizontal-radius('top', 'right', 0);
		@include border-vertical-horizontal-radius('bottom', 'right', 0);
	}

	&--no-radius-bottom {
		@include border-vertical-horizontal-radius('bottom', 'left', 0);
		@include border-vertical-horizontal-radius('bottom', 'right', 0);
	}

	&--no-radius-left {
		@include border-vertical-horizontal-radius('top', 'left', 0);
		@include border-vertical-horizontal-radius('bottom', 'left', 0);
	}
}

//remove opacity on ios devices as it causes flickering
html.ios .title-poster:active {
	opacity: unset;
}
</style>
